import * as Sentry from '@sentry/react';
import { DsChat } from 'dragonmouse-shared/interfaces';
import { VITE_FORGE_URL } from '@constants';
import { resolveOptimisticallyUpdatedDsChats, updateActiveDsChat } from '../redux/db/actions';
import store from '../redux/store';
import { PollingStream } from './PollingStream';

export class DsChatPollingStream extends PollingStream<DsChat> {
	constructor(dsChatId: string) {
		const url = VITE_FORGE_URL + `/poll/ds-chat/${dsChatId}`;

		const onError = (error: Error) => {
			if (error.message === 'Access denied') {
				store.dispatch(updateActiveDsChat(null));
			} else {
				console.error(error);
			}
		};

		super(url, onError, 'activeDsChat');
	}

	extractData(response: any): DsChat[] {
		// DS chat endpoint returns a single DS chat object or null
		if (response?.dsChat === null || response?.dsChat === undefined) {
			return [];
		}

		return [response.dsChat];
	}

	onData(dsChats: DsChat[]) {
		// Resolve optimistic writes based on writeId from the database
		const dsChat = dsChats.length > 0 ? dsChats[0] : null;
		if (!dsChat) {
			store.dispatch(updateActiveDsChat(null));
			return;
		}

		const writeId = dsChat.writeId;
		if (writeId) {
			store.dispatch(resolveOptimisticallyUpdatedDsChats([writeId]));
		}

		// The backend already returns DsChat objects with proper camelCase field names
		// (thanks to postgres.camel transform)
		// We just need to convert date strings to Date objects
		const processedDsChat: DsChat = {
			...dsChat,
			createdAt: new Date(dsChat.createdAt),
			updatedAt: new Date(dsChat.updatedAt),
		};

		store.dispatch(updateActiveDsChat(processedDsChat));

		// Detect if optimistic updates have been applied
		// Get all write IDs that were resolved by the server
		const resolvedWriteIds = new Set(writeId ? [writeId] : []);

		// Check for any optimistic writes that weren't resolved
		const state = store.getState();
		const currentChatId = dsChat.id;
		const unresolvedWrites = state.db.optimisticDsChatWrites
			.filter((write) => write.value.id === currentChatId)
			.filter((write) => !resolvedWriteIds.has(write.id));

		if (unresolvedWrites.length > 0) {
			const now = Date.now();
			unresolvedWrites.forEach((write) => {
				if (write.value.createdAt instanceof Date) {
					const age = now - write.value.createdAt.getTime();
					if (age > 2500) {
						const message =
							`[PollingStream] Optimistic ${write.operation} for dsChat ${write.value.id} ` +
							`not resolved after ${age}ms (writeId: ${write.id})`;
						console.warn(message);
						Sentry.captureMessage(message, 'warning');
					}
				}
			});
		}
	}
}
